/**
 * The posting guide: what a campaign's suppliers are told to send.
 *
 * Two views over one model (`postingGuide.ts`):
 *
 * - `CampaignPostingGuideTab`, the operator's side: every field the
 *   campaign's recent leads carry, plus any declared ones, each with a
 *   description, an example, whether it is in the guide, and under Advanced
 *   the `source_path` intake reads it from. Saved to `settings.fields`
 *   through `PUT /api/v1/campaigns/{id}/fields` (edit_campaigns).
 * - `CampaignSupplierGuideTab`, what the supplier reads: endpoint, key,
 *   campaign id, the included fields and a request to copy. It replaces the
 *   old API tab, so there is one guide, not two.
 *
 * `required` and `validation`, which the old Fields tab offered, are gone
 * from the screen: nothing at intake has ever enforced them. The server
 * keeps whatever is stored.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Code, Collapse, CopyButton, Group, Loader,
  Modal, ScrollArea, Select, Stack, Switch, Table, Tabs, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck, IconChevronDown, IconChevronRight, IconCopy, IconEye, IconEyeOff, IconPlus,
  IconRefresh, IconTrash,
} from '@tabler/icons-react';
import { useCampaignDetail, useResetApiKey, useUpdateCampaignFields } from '../api/queries';
import { useLeadFields, useLeadFieldValuesFor } from '../api/leadFields';
import { getApiUrl } from '../api/client';
import { canDo } from '../layout/commands';
import { useNavUser } from '../layout/navUser';
import { useVocab } from '../vocab';
import {
  FIELD_TYPES, curlExample, exampleBody, exampleFor, fieldsToSave, guideText, inferType,
  jsExample, mergeFields, needsValues, pythonExample, type GuideEntry, type GuideField,
} from './postingGuide';

type CampaignDetail = NonNullable<ReturnType<typeof useCampaignDetail>['data']>;

const TYPE_OPTIONS = FIELD_TYPES.map((t) => ({ value: t, label: t }));

function pct(coverage: number): string {
  const p = coverage * 100;
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1)}%`;
}

function CopyIcon({ value, label = 'Copy' }: { value: string; label?: string }) {
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Copied' : label}>
          <ActionIcon variant="subtle" color="gray" onClick={copy} aria-label={label}>
            {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}

/**
 * The rows for a campaign as saved: its declared fields merged with what its
 * leads carried in the last 30 days, and the commonest values of the fields
 * whose example would come from them.
 */
function useGuideRows(campaign: CampaignDetail) {
  const seen = useLeadFields([campaign.id]);
  const rows = useMemo(
    () => mergeFields(campaign.fields, seen.data?.fields ?? []),
    [campaign.fields, seen.data],
  );
  const valueKeys = useMemo(() => rows.filter(needsValues).map((r) => r.key), [rows]);
  const values = useLeadFieldValuesFor(valueKeys, [campaign.id]);
  return { rows, values, seen };
}

// ---------------------------------------------------------------------------
// The operator's side
// ---------------------------------------------------------------------------

export function CampaignPostingGuideTab({
  campaign,
  onPreview,
}: {
  campaign: CampaignDetail;
  onPreview?: () => void;
}) {
  const v = useVocab();
  const canEdit = canDo(useNavUser(), 'edit_campaigns');
  const save = useUpdateCampaignFields(campaign.id);
  const { rows: loaded, values, seen } = useGuideRows(campaign);
  const [rows, setRows] = useState<GuideField[]>(loaded);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState<Record<number, boolean>>({});

  // Fresh data replaces the rows unless the operator is part way through.
  useEffect(() => {
    if (!dirty) setRows(loaded);
  }, [loaded, dirty]);

  const update = (i: number, patch: Partial<GuideField>) => {
    setDirty(true);
    setRows((rs) =>
      rs.map((r, j) => {
        if (j !== i) return r;
        // A seen field is declared on its first change, typed from its values.
        const typed = !r.declared && !r.touched ? { type: inferType(r.key, values[r.key]) } : {};
        return { ...r, ...typed, ...patch, touched: true };
      }),
    );
  };

  const names = rows.map((r) => r.key.trim()).filter(Boolean);
  const duplicated = new Set(names).size !== names.length;
  const invalid = rows.some((r) => r.key.trim() && !/^[A-Za-z0-9_]+$/.test(r.key.trim()));
  const included = rows.filter((r) => r.in_guide && r.key.trim()).length;

  async function submit() {
    try {
      await save.mutateAsync({ fields: fieldsToSave(rows) });
      setDirty(false);
      notifications.show({ color: 'teal', title: 'Posting guide saved', message: `${included} field(s) in the guide.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: (e as Error).message });
    }
  }

  function discard() {
    setRows(loaded);
    setDirty(false);
  }

  return (
    <Stack>
      <Alert color="gray" variant="light">
        <Text size="sm">
          {`What ${v.many('supplier')} are told to send to this ${v.one('campaign')}. Fields come from the ${v.many('lead')} it received in the last 30 days, plus any you have added. Examples never show a real person's details: personal fields use a placeholder, and other values only appear once several ${v.many('lead')} share them. The guide describes fields; it does not reject ${v.many('lead')} that leave one out.`}
        </Text>
      </Alert>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {seen.isPending
            ? `Reading recent ${v.many('lead')}…`
            : seen.data
              ? `${rows.length} field(s), ${included} in the guide. Sampled from ${v.count(seen.data.sample_rows, 'lead')} in the last ${seen.data.days} days.`
              : `${rows.length} field(s), ${included} in the guide.`}
        </Text>
        {onPreview && (
          <Anchor component="button" type="button" size="sm" onClick={onPreview}>
            {`See the ${v.one('supplier')} guide`}
          </Anchor>
        )}
      </Group>

      {seen.error && (
        <Alert color="chartAmber" variant="light">
          <Text size="sm">{`Recent ${v.many('lead')} could not be read, so only the fields you have added are listed. ${seen.error.message}`}</Text>
        </Alert>
      )}

      {seen.isPending && rows.length === 0 ? (
        <Group justify="center" p="lg"><Loader size="sm" /></Group>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed">{`No fields yet. None of this ${v.one('campaign')}'s ${v.many('lead')} in the last 30 days carried any, and none have been added.`}</Text>
      ) : (
        <Table.ScrollContainer minWidth={860}>
          <Table verticalSpacing="xs" data-testid="posting-guide-fields">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={230}>Field</Table.Th>
                <Table.Th>{`Description for ${v.many('supplier')}`}</Table.Th>
                <Table.Th w={220}>Example</Table.Th>
                <Table.Th w={80}>In guide</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r, i) => {
                const auto = exampleFor({ ...r, example: '' }, values[r.key]);
                const added = !r.declared && r.coverage === null && r.touched;
                return [
                  <Table.Tr key={`${i}-row`}>
                    <Table.Td>
                      {added ? (
                        <TextInput
                          size="xs"
                          placeholder="field_name"
                          value={r.key}
                          error={!r.key.trim() || (r.key.trim() !== '' && !/^[A-Za-z0-9_]+$/.test(r.key.trim()))}
                          onChange={(e) => update(i, { key: e.currentTarget.value })}
                          disabled={!canEdit}
                          aria-label="Field name"
                        />
                      ) : (
                        <Group gap={6} wrap="nowrap">
                          <Code>{r.key}</Code>
                          {r.source_path && (
                            <Tooltip label={`Read from ${r.source_path}`}>
                              <Badge size="xs" variant="light" color="gray">mapped</Badge>
                            </Tooltip>
                          )}
                        </Group>
                      )}
                      <Text size="xs" c="dimmed" mt={4}>
                        {r.coverage !== null
                          ? `Seen on ${pct(r.coverage)} of ${v.many('lead')}`
                          : added
                            ? 'New field'
                            : 'Not seen in the last 30 days'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="What this field holds"
                        value={r.description}
                        onChange={(e) => update(i, { description: e.currentTarget.value })}
                        disabled={!canEdit}
                        aria-label={`Description for ${r.key || 'new field'}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder={auto.value}
                        value={r.example}
                        onChange={(e) => update(i, { example: e.currentTarget.value })}
                        disabled={!canEdit}
                        aria-label={`Example for ${r.key || 'new field'}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        size="xs"
                        checked={r.in_guide}
                        onChange={(e) => update(i, { in_guide: e.currentTarget.checked })}
                        disabled={!canEdit}
                        aria-label={`Include ${r.key || 'new field'} in the guide`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Advanced">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Advanced settings for ${r.key || 'new field'}`}
                          aria-expanded={!!open[i]}
                          onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                        >
                          {open[i] ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>,
                  <Table.Tr key={`${i}-advanced`} style={{ borderTop: open[i] ? undefined : 'none' }}>
                    <Table.Td colSpan={5} p={0}>
                      <Collapse in={!!open[i]}>
                        <Group align="flex-end" gap="md" p="sm" wrap="wrap">
                          <Select
                            size="xs"
                            label="Type"
                            w={130}
                            data={TYPE_OPTIONS}
                            value={r.type}
                            allowDeselect={false}
                            onChange={(t) => t && update(i, { type: t })}
                            disabled={!canEdit}
                          />
                          <TextInput
                            size="xs"
                            label="Source path"
                            description="Where intake reads this field in a nested body, such as contact.email. Blank reads the top level."
                            placeholder="blank = top level"
                            value={r.source_path}
                            onChange={(e) => update(i, { source_path: e.currentTarget.value })}
                            disabled={!canEdit}
                            style={{ flex: 1, minWidth: 260 }}
                          />
                          {canEdit && (r.declared || added) && (
                            <Button
                              size="xs"
                              variant="subtle"
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => {
                                setDirty(true);
                                setOpen({});
                                setRows((rs) => rs.filter((_, j) => j !== i));
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </Group>
                        {r.declared && (
                          <Text size="xs" c="dimmed" px="sm" pb="sm">
                            {`Removing a field stops its source path being used on new ${v.many('lead')}. It does not delete it from ${v.many('lead')} already stored, and it comes back here while ${v.many('lead')} still carry it.`}
                          </Text>
                        )}
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>,
                ];
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {(duplicated || invalid) && (
        <Alert color="chartAmber" variant="light">
          <Text size="sm">
            {duplicated && 'Two fields share a name. '}
            {invalid && 'Field names can only use letters, numbers and underscores.'}
          </Text>
        </Alert>
      )}

      {canEdit && (
        <Group justify="space-between">
          <Button
            variant="light"
            leftSection={<IconPlus size={15} />}
            onClick={() => {
              setDirty(true);
              setRows((rs) => [
                ...rs,
                {
                  key: '', type: 'text', source_path: '', description: '', example: '',
                  in_guide: true, declared: false, coverage: null, leads_with: 0, touched: true,
                },
              ]);
            }}
          >
            Add field
          </Button>
          <Group gap="xs">
            {dirty && <Button variant="subtle" color="gray" onClick={discard}>Discard changes</Button>}
            <Button
              loading={save.isPending}
              disabled={!dirty || duplicated || invalid}
              onClick={submit}
            >
              Save guide
            </Button>
          </Group>
        </Group>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// What the supplier reads
// ---------------------------------------------------------------------------

export function CampaignSupplierGuideTab({ campaign }: { campaign: CampaignDetail }) {
  const v = useVocab();
  const reset = useResetApiKey(campaign.id);
  const { rows, values, seen } = useGuideRows(campaign);
  const [revealed, setRevealed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lang, setLang] = useState('json');

  const url = `${getApiUrl()}/api/leads/submit`;
  const keyRequired = campaign.settings.require_api_key === true;
  // The real key goes into the examples only while it is on screen.
  const key = revealed && campaign.api_key ? campaign.api_key : 'YOUR_API_KEY';
  const masked = campaign.api_key
    ? `${campaign.api_key.slice(0, 6)}${'•'.repeat(18)}${campaign.api_key.slice(-4)}`
    : null;

  const entries: GuideEntry[] = rows
    .filter((r) => r.in_guide)
    .map((r) => ({
      key: r.key,
      type: r.type === 'text' && !r.declared ? inferType(r.key, values[r.key]) : r.type,
      description: r.description,
      example: exampleFor(r, values[r.key]).value,
    }));
  const body = exampleBody(campaign.id, entries);
  const code =
    lang === 'json'
      ? JSON.stringify(body, null, 2)
      : lang === 'curl'
        ? curlExample(url, key, body)
        : lang === 'javascript'
          ? jsExample(url, key, body)
          : pythonExample(url, key, body);
  const title = `${v.One('supplier')} guide: ${campaign.name}`;
  const text = guideText({ title, url, campaignId: campaign.id, keyRequired, entries, v });

  async function doReset() {
    try {
      await reset.mutateAsync();
      setRevealed(true);
      setConfirming(false);
      notifications.show({
        color: 'teal',
        title: 'API key rotated',
        message: `New key issued. Give it to every ${v.one('supplier')} posting to ${campaign.name} now.`,
        autoClose: false,
      });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Reset failed', message: (e as Error).message });
    }
  }

  return (
    <Stack maw={960}>
      <Group justify="space-between" align="flex-start">
        <div>
          <Text fw={600}>{title}</Text>
          <Text size="sm" c="dimmed">
            {`How to send ${v.many('lead')} to this ${v.one('campaign')}. Copy it into an email to ${v.a('supplier')}; the key is never included.`}
          </Text>
        </div>
        <CopyButton value={text}>
          {({ copied, copy }) => (
            <Button
              variant="light"
              leftSection={copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
              onClick={copy}
            >
              {copied ? 'Copied' : 'Copy guide'}
            </Button>
          )}
        </CopyButton>
      </Group>

      <Card withBorder padding="sm">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">URL</Text>
        <Group gap="xs" mt={4}>
          <Badge color="chartGreen" variant="light">POST</Badge>
          <Code>{url}</Code>
          <CopyIcon value={url} />
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          A JSON body, with <Code>Content-Type: application/json</Code>.
        </Text>
      </Card>

      <Card withBorder padding="sm">
        <Group justify="space-between" align="flex-start">
          <div style={{ minWidth: 0 }}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">{`${v.One('campaign')} id`}</Text>
            <Group gap="xs" mt={4}>
              <Code>{campaign.id}</Code>
              <CopyIcon value={campaign.id} />
            </Group>
            <Text size="xs" c="dimmed" mt={4}>
              Sent as <Code>campaign_id</Code> in the body.
            </Text>
          </div>
        </Group>
      </Card>

      <Card withBorder padding="sm">
        <Group justify="space-between" align="flex-start">
          <div style={{ minWidth: 0 }}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">API key</Text>
            {campaign.api_key ? (
              <Group gap="xs" mt={4}>
                <Code>{revealed ? campaign.api_key : masked}</Code>
                <Tooltip label={revealed ? 'Hide' : 'Reveal'}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setRevealed(!revealed)}
                    aria-label={revealed ? 'Hide key' : 'Reveal key'}
                  >
                    {revealed ? <IconEyeOff size={15} /> : <IconEye size={15} />}
                  </ActionIcon>
                </Tooltip>
                <CopyIcon value={campaign.api_key} label="Copy key" />
              </Group>
            ) : (
              <Text size="sm" c="dimmed" mt={4}>
                {`Not shown, because your account cannot edit ${v.many('campaign')}.`}
              </Text>
            )}
            <Text size="xs" c="dimmed" mt={4}>
              {'Sent in the '}<Code>X-API-Key</Code>
              {keyRequired
                ? ` header. This ${v.one('campaign')} rejects ${v.many('lead')} without it.`
                : ` header. This ${v.one('campaign')} does not require it yet, but ${v.many('supplier')} should send it.`}
            </Text>
          </div>
          {campaign.api_key && (
            <Button
              variant="light"
              color="red"
              leftSection={<IconRefresh size={15} />}
              onClick={() => setConfirming(true)}
            >
              Reset key
            </Button>
          )}
        </Group>
      </Card>

      <div>
        <Text size="sm" fw={600} mb="xs">Fields</Text>
        {seen.isPending && entries.length === 0 ? (
          <Group justify="center" p="md"><Loader size="sm" /></Group>
        ) : entries.length === 0 ? (
          <Text size="sm" c="dimmed">No fields are in the guide yet. Add them on the Posting guide tab.</Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing="xs" data-testid="supplier-guide-fields">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={200}>Field</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th w={240}>Example</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {entries.map((e) => (
                  <Table.Tr key={e.key}>
                    <Table.Td><Code>{e.key}</Code></Table.Td>
                    <Table.Td>
                      <Text size="sm" c={e.description ? undefined : 'dimmed'}>
                        {e.description || 'No description'}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="sm" ff="monospace">{e.example}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </div>

      <div>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={600}>Example request</Text>
          <CopyButton value={code}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                onClick={copy}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Tabs value={lang} onChange={(l) => l && setLang(l)}>
          <Tabs.List mb="xs">
            <Tabs.Tab value="json">JSON body</Tabs.Tab>
            <Tabs.Tab value="curl">curl</Tabs.Tab>
            <Tabs.Tab value="javascript">JavaScript</Tabs.Tab>
            <Tabs.Tab value="python">Python</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <ScrollArea.Autosize mah={380}>
          <Code block data-testid="supplier-guide-code">{code}</Code>
        </ScrollArea.Autosize>
      </div>

      <Alert color="gray" variant="light" title="What the response means">
        <Text size="sm">
          A post returns <Code>200</Code> with a <Code>status</Code> of{' '}
          <Code>accepted</Code>, <Code>duplicate</Code>, <Code>suppressed</Code> or{' '}
          <Code>error</Code>
          {`. Those are outcomes of a request that was processed successfully, not transport failures, so ${v.a('supplier')} should branch on `}
          <Code>status</Code>
          {` rather than on the HTTP code. Genuine failures (a bad key, a missing field, an inactive ${v.one('campaign')}) come back as a 4xx problem document.`}
        </Text>
      </Alert>

      <Modal opened={confirming} onClose={() => setConfirming(false)} title="Reset this API key?">
        <Stack>
          <Alert color="red" variant="light" title="This breaks live ingestion immediately">
            <Text size="sm">
              {`The current key stops working the moment you confirm. Every ${v.one('supplier')} posting to ${campaign.name} will get rejected until you give them the new one. There is no grace period and the old key cannot be restored.`}
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button color="red" loading={reset.isPending} onClick={doReset}>
              Reset key
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
